// src/components/ChatWidget.jsx
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { ChatContext } from '../context/ChatContext';
import API_URL from '../config';

const COLLAPSED_SIZE = 60;
const EXPANDED_WIDTH = 350;
const EXPANDED_HEIGHT = 450;

const ChatWidget = () => {
  const { chatDictionary } = useContext(ChatContext);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState(null); // null = not yet initialised for this dictionary
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const conversationRef = useRef(null);
  const textareaRef = useRef(null);
  const dictionaryKeyRef = useRef(null); // track which dictionary the messages belong to

  // Scroll to bottom whenever messages change.
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // When the widget opens (or the dictionary changes), initialise a fresh conversation
  // and fire the initial API call to get the opening assistant message.
  const initialiseChat = useCallback(async () => {
    const dictKey = JSON.stringify(chatDictionary);
    if (dictionaryKeyRef.current === dictKey) return; // already initialised for this dictionary
    dictionaryKeyRef.current = dictKey;

    const systemContent = await fetchSystemMessage(chatDictionary);
    const initialMessages = [{ role: 'system', content: systemContent }];
    setMessages(initialMessages);
    setLoading(true);

    try {
      const reply = await callChatApi(initialMessages);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + e.message }]);
    } finally {
      setLoading(false);
    }
  }, [chatDictionary]);

  const handleOpen = () => {
    setExpanded(true);
    initialiseChat();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setExpanded(false);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const reply = await callChatApi(newMessages);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + e.message }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Visible messages are everything except the hidden system message.
  const visibleMessages = messages ? messages.filter(m => m.role !== 'system') : [];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: expanded ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_SIZE}px`,
        height: expanded ? `${EXPANDED_HEIGHT}px` : `${COLLAPSED_SIZE}px`,
        borderRadius: expanded ? '10px' : '50%',
        backgroundColor: 'rgb(31, 41, 55)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        zIndex: 10000,
        transition: 'width 0.3s ease, height 0.3s ease, border-radius 0.3s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Collapsed toggle button */}
      {!expanded && (
        <div
          onClick={handleOpen}
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '24px',
            cursor: 'pointer',
          }}
        >
          &#128172;
        </div>
      )}

      {/* Expanded chat UI */}
      {expanded && (
        <>
          {/* Header */}
          <div style={{
            backgroundColor: 'rgb(31, 41, 55)',
            color: '#fff',
            padding: '10px 15px',
            fontSize: '0.8em',
            textAlign: 'center',
            flexShrink: 0,
            position: 'relative',
          }}>
            MARBLE Chat
            <span
              onClick={handleClose}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#aaa',
                fontSize: '16px',
              }}
            >
              &times;
            </span>
          </div>

          {/* Conversation */}
          <div
            ref={conversationRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '10px',
              backgroundColor: '#fff',
            }}
          >
            {visibleMessages.map((msg, i) => (
              <div key={i} style={{ width: '100%', textAlign: msg.role === 'user' ? 'right' : 'left', marginBottom: '4px' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '8px 10px',
                  borderRadius: '5px',
                  maxWidth: '90%',
                  wordWrap: 'break-word',
                  fontSize: '0.85em',
                  backgroundColor: msg.role === 'user' ? '#e8f4fd' : '#f5f5f5',
                }}>
                  {msg.content}
                </span>
              </div>
            ))}

            {loading && (
              <div style={{ textAlign: 'left', marginBottom: '4px' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '8px 10px',
                  borderRadius: '5px',
                  backgroundColor: '#f5f5f5',
                }}>
                  <LoadingDots />
                </span>
              </div>
            )}
          </div>

          {/* Input row */}
          <div style={{
            display: 'flex',
            padding: '8px',
            borderTop: '1px solid #ddd',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}>
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '8px',
                fontSize: '0.85em',
                border: '1px solid #ccc',
                borderRadius: '5px',
                resize: 'none',
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading}
              style={{
                marginLeft: '8px',
                padding: '8px 14px',
                fontSize: '0.85em',
                backgroundColor: 'rgb(31, 41, 55)',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Animated loading dots
const LoadingDots = () => (
  <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
    {[0, 1, 2].map(i => (
      <span
        key={i}
        style={{
          display: 'inline-block',
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          backgroundColor: '#666',
          animation: 'chatBounce 1.4s infinite ease-in-out',
          animationDelay: `${[-0.32, -0.16, 0][i]}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes chatBounce {
        0%, 80%, 100% { transform: scale(0); }
        40% { transform: scale(1); }
      }
    `}</style>
  </span>
);

// Ask Django's chat app to render the system message for the given dictionary.
async function fetchSystemMessage(dictionary) {
  try {
    const res = await fetch(`${API_URL}/chat/system-message/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ dictionary }),
    });
    if (!res.ok) throw new Error('Failed to load system message');
    const data = await res.json();
    return data.system_message;
  } catch {
    return 'You are a helpful assistant who explains sleep staging.';
  }
}

// Send the full conversation history to Django and get a reply.
async function callChatApi(messages) {
  const res = await fetch(`${API_URL}/chat/api/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ messages }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'API error');
  return data.reply;
}

export default ChatWidget;
